/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaApplyDrag.ts
 * FILE_ID: JOBHUB-QA-APPLY-DRAG-001
 * PURPOSE: Run one drag drop through the existing reorder and move calls.
 */
import { areaItems, checklistGroups, normalizeList } from '../../qa/qaApiHelpers'
import { toSortOrderPayload } from '../../qa/checklistTable'
import { commitQaDrop, type QaDropRef } from '../../qa/qaDragCommit'
import type { QaArea, QaAreaExecution, QaFloor } from '../../qa/qaApi'
import type { JobQaTabController } from './useJobQaTabController'

export function useQaApplyDrag(controller: JobQaTabController) {
  const { api, tree, workspace } = controller
  return (from: QaDropRef, to: QaDropRef) => {
    const floors = normalizeList<QaFloor>(workspace.floorsQuery.data)
    const areas = normalizeList<QaArea>(workspace.areasQuery.data)
    const execution = workspace.areaExecQuery.data as QaAreaExecution | undefined
    const checklists = execution
      ? checklistGroups(execution).map((group) => ({ id: group.checklist.id, areaId: execution.id }))
      : []
    const items = execution
      ? areaItems(execution).map((item) => ({
        id: item.id,
        parent_item: item.parent_item,
        checklistId: item.checklist_instance ?? '',
      }))
      : []
    const command = commitQaDrop(from, to, {
      floors,
      areas: areas.map((area) => ({ id: area.id, floorId: area.floor ?? '' })),
      checklists,
      items,
    })
    if (!command) return
    if (command.type === 'reorder-floors') {
      void api.reorderFloors(command.order).then(() => workspace.floorsQuery.refetch())
      return
    }
    if (command.type === 'reorder-areas') {
      tree.reorder.mutate({ nodeType: 'area', order: command.order })
      return
    }
    if (command.type === 'move-areas') {
      tree.move.mutate({ nodeType: 'area', nodeIds: command.nodeIds, destination: { parentId: command.parentId } })
      return
    }
    if (command.type === 'reorder-checklists') {
      tree.reorder.mutate({ nodeType: 'checklist_instance', order: command.order })
      return
    }
    if (command.type === 'move-checklists') {
      tree.move.mutate({
        nodeType: 'checklist_instance',
        nodeIds: command.nodeIds,
        destination: { parentId: command.parentId },
      })
      return
    }
    if (command.type === 'reorder-items') {
      void api.reorderItems(toSortOrderPayload(command.ids)).then(() => workspace.invalidateExecution())
      return
    }
    tree.move.mutate({
      nodeType: 'item_instance',
      nodeIds: command.nodeIds,
      destination: { parentId: command.parentId },
    })
  }
}
